import { h, Fragment } from 'preact'
import { signal } from '@preact/signals'
import { useState, useEffect, useRef } from 'preact/hooks'
import { Slugify } from '../../js/modules/utilities.js'

import { TuileArgument } from '../tuile-argument/tuile-argument.jsx'
import { TuileAiguillage } from '../tuile-aiguillage/tuile-aiguillage.jsx'
import { TuileDevice } from '../tuile-device/tuile-device.jsx'
import { TuileMessage } from '../tuile-message/tuile-message.jsx'
import { TuileOffre } from '../tuile-offre/tuile-offre.jsx'
import { TuileOffre2 } from '../tuile-offre-2/tuile-offre-2.jsx'
import { TuileServicielle } from '../tuile-servicielle/tuile-servicielle.jsx'

// Swiper JS
import Swiper from 'swiper'
import { Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

export const vitrineState = signal('')

const getPriceSize = (items) => {
   for (const { items: tabItems } of items) {
      for (const { template, price } of tabItems) {
         if (template === 'device' && price.length > 6) {
            return true
         }
      }
   }
   return false
}

export const Vitrine = ({ title, liensVoirTout = true, items, grid, hideTitle = false, cacheKey }) => {

   // SIGNALS
   // Récupérer les data et les injecter au bon endroit dans la tuile dont l'id correspond du nom du signal
   const positionId = Object.keys(vitrineState.value)[0];
   if (vitrineState.value !== '' && vitrineState.value[positionId] !== undefined) {
      const selectedTile = items[0].items.find(item => item.id === positionId);
      if (selectedTile) {
         Object.assign(selectedTile, vitrineState.value[positionId]);
      }
   }

   let swiper
   const priceSmall = getPriceSize(items)
   const [isNavigating, setIsNavigating] = useState(false)
   const [selectedTab, setSelectedTab] = useState(() => {
      const hash = window.location.hash.substring(1)
      const index = items.findIndex((item) => Slugify(item.title) === hash)
      return items[index] || items[0]
   })

   // Gestion de l'accessibilité au tab et aux touches flèches
   const listRef = useRef(null)

   useEffect(() => {
      const listElement = listRef.current
      if (listElement !== null) {
         listElement.querySelectorAll("[role='tab']").forEach((tab, index, tabs) => {
            tab.addEventListener('keydown', (e) => {
               let newIndex
               if (e.key === 'ArrowRight') {
                  newIndex = (index + 1) % tabs.length
               } else if (e.key === 'ArrowLeft') {
                  newIndex = (index - 1 + tabs.length) % tabs.length
               }
               if (newIndex !== undefined) {
                  tabs[newIndex].focus()
                  tabs[newIndex].click()
               }
            })
         })
      }

      swiper = new Swiper('.swiper', {
         modules: [Navigation, Pagination],
         navigation: {
            enabled: false,
            nextEl: '.swiper-button-aloha-next',
            prevEl: '.swiper-button-aloha-prev',
         },
         pagination: {
            enabled: true,
            el: '.swiper-pagination',
            clickable: true,
            bulletClass: 'swiper-pagination-aloha',
            bulletActiveClass: 'swiper-pagination-aloha-active',
            horizontalClass: 'swiper-pagination-aloha-horizontal',
            renderBullet: function (index, className) {
               return '<span class="' + className + '"><span></span></span>'
            },
         },
         direction: 'horizontal',
         preventClicks: false,
         loop: false,
         slidesOffsetBefore: 15,
         slidesOffsetAfter: 15,
         spaceBetween: 30,
         slidesPerView: 'auto',
         breakpoints: {
            // when window width is >= 736px
            736: {
               pagination: {
                  enabled: true,
               },
               navigation: {
                  enabled: true,
               },
               slidesOffsetBefore: 30,
            },
            // when window width is >= 960px
            960: {
               navigation: {
                  enabled: false,
               },
               pagination: {
                  enabled: false,
               },
               slidesOffsetBefore: 0,
               allowTouchMove: false,
            },
         },
      })

      const handleResize = () => {
         swiper.forEach((s) => {
            s?.slideTo(0)
         })
      }

      // Gestion de la navigation avec les flèches du navigateur
      const handleHistoryChange = () => {
         const hash = window.location.hash.substring(1)
         const index = items.findIndex((item) => Slugify(item.title) === hash)
         setIsNavigating(true)
         setSelectedTab(items[index] || items[0])
      }

      window.addEventListener('popstate', handleHistoryChange)
      window.addEventListener('resize', handleResize)

      return () => {
         window.removeEventListener('popstate', handleHistoryChange)
         window.removeEventListener('resize', handleResize)
      }
   }, [])

   useEffect(() => {
      // Update le hash de l'URL avec l'onglet sélectionné (ne pas exécuter si on est en train de naviguer avec les flèches du navigateur)
      if (!isNavigating) {
         if (selectedTab && items.indexOf(selectedTab) !== 0) {
            window.history.pushState(null, null, `#${Slugify(selectedTab.title)}`)
         } else {
            window.history.pushState(null, null, ' ')
         }
      }
      setIsNavigating(false)
   }, [selectedTab])

   return (
      <div class="component-vitrine pb-0 pb-lg-1 pt-2 bg-gray2" data-cache-key={cacheKey}>
         {title && !hideTitle && (
            <div class="container-fluid">
               <h2 class="no-signature text-center mw-100">{title}</h2>
            </div>
         )}
         {items.length > 1 && (
            <div class="container-fluid">
               <nav class="tabs-menu ob1-menu-page ob1-menu-page-center mb-1">
                  <ul class="nav o-nav-light" ref={listRef} role="tablist">
                     {items.map((tab, index) => (
                        <li key={index} role="presentation" class={`nav-item ${tab === selectedTab ? 'active' : ''}`}>
                           <button
                              role="tab"
                              aria-controls={`vitrine-tab${index}-content`}
                              aria-selected={tab === selectedTab ? 'true' : 'false'}
                              onClick={() => setSelectedTab(tab)}
                              class="nav-link btn"
                              id={`vitrine-tab${index}`}
                              tabIndex={tab === selectedTab ? '0' : '-1'}
                              data-pdb-zid={tab.analytics.pdbZid}
                              data-pdb-sc={tab.analytics.pdbSc}
                              data-pdb-idc={tab.analytics.pdbIdc}
                              data-pdb-vi={tab.analytics.pdbVi}
                              data-oevent-category={tab.analytics.oeventCategory}
                              data-oevent-action={tab.analytics.oeventAction}
                              data-oevent-label={tab.analytics.oeventLabel}
                              data-oevent-other={tab.analytics.oeventOther}
                           >
                              {tab.title}
                           </button>
                        </li>
                     ))}
                  </ul>
               </nav>
            </div>
         )}
         <div class="tabs-container">
            {items.map((tab, index) => (
               <Fragment key={index}>
                  <div class="container-fluid d-flex justify-content-between mt-1 mt-lg-0">
                     {tab.title && <h2 class="h3 tab-title mb-0 mt-0">{tab.title}</h2>}
                     {liensVoirTout && tab.lien_aiguillage && (
                        <a
                           href={tab.lien_aiguillage.URL}
                           class="lien-voir-tout o-link-arrow d-lg-none"
                           data-pdb-zid={tab.lien_aiguillage.analytics.pdbZid}
                           data-pdb-sc={tab.lien_aiguillage.analytics.pdbSc}
                           data-pdb-idc={tab.lien_aiguillage.analytics.pdbIdc}
                           data-pdb-vi={tab.lien_aiguillage.analytics.pdbVi}
                           data-oevent-category={tab.lien_aiguillage.analytics.oeventCategory}
                           data-oevent-action={tab.lien_aiguillage.analytics.oeventAction}
                           data-oevent-label={tab.lien_aiguillage.analytics.oeventLabel}
                           data-oevent-other={tab.lien_aiguillage.analytics.oeventOther}
                        >
                           {tab.lien_aiguillage.label}
                        </a>
                     )}
                  </div>
                  <div class="container-fluid only-after-960">
                     <div
                        class={`tab-content grid-${grid.name} ${tab === selectedTab ? 'active' : ''}`}
                        role="tabpanel"
                        id={`vitrine-tab${index}-content`}
                        aria-labelledby={`vitrine-tab${index}`}
                     >
                        <div class="swiper">
                           <div class="swiper-wrapper">
                              {tab.items.map((tile, index) => (
                                 <div key={index} class={`swiper-slide tile tile${index + 1} ${grid.tilesSize[index]}`}>
                                    {renderTile(tile, priceSmall)}
                                 </div>
                              ))}
                           </div>

                           <div class="swiper-pagination" aria-hidden="true"></div>

                           <div class="swiper-button-aloha swiper-button-aloha-prev" aria-hidden="true">
                              <span class="icon-arrow-previous"></span>
                           </div>
                           <div class="swiper-button-aloha swiper-button-aloha-next" aria-hidden="true">
                              <span class="icon-arrow-next"></span>
                           </div>
                        </div>
                     </div>
                  </div>
               </Fragment>
            ))}
         </div>
      </div>
   )
}

const renderTile = (props, priceSmall) => {
   const { template, ...restOfProps } = props

   if (template === 'device') {
      restOfProps.price_small = priceSmall
   }
   const components = {
      advise: Placeholder,
      argument: TuileArgument,
      aiguillage: TuileAiguillage,
      device: TuileDevice,
      message: TuileMessage,
      offre: TuileOffre,
      offre_2: TuileOffre2,
      servicielle: TuileServicielle,
      servicielle_simple: TuileServicielle, // TODO @Jérémy: coté back, pas besoin de spécifier simple/double
      servicielle_double: TuileServicielle, // TODO @Jérémy: coté back, pas besoin de spécifier simple/double
   }
   const Component = components[template]
   return Component ? <Component {...restOfProps} /> : null
}

const Placeholder = ({ id }) => {
   return <div class="tile-placeholder" id={id}></div>
}
